import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { useClassFilter } from '@/contexts/ClassFilterContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, FileText, DollarSign, AlertTriangle, ArrowLeft, ChevronRight, Download, Shield, Clock, Plus, X, Users } from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import { useSort, SortableTh } from '@/components/SortableTh';
import { RegistryLink } from '@/components/RegistryLink';
import EmailDocButton from '@/components/EmailDocButton';
import PortalLinkButton from '@/components/PortalLinkButton';
import { toast } from 'sonner';
import SavedViews from '@/components/SavedViews';
import RecordDocuments from '@/components/RecordDocuments';
import EndorsementsCard from '@/components/EndorsementsCard';
import { CancellationNoticesCard } from '@/components/CancellationNoticesCard';
import OpenCoverPanel from '@/components/OpenCoverPanel';
import BuyerLedgerPanel from '@/components/BuyerLedgerPanel';
import CropSeasonPanel from '@/components/CropSeasonPanel';

const STATUS_COLORS = {
  active: { bg: 'rgba(29,158,117,0.1)', text: '#1D9E75' },
  cancelled: { bg: 'rgba(226,75,74,0.1)', text: '#E24B4A' },
  expired: { bg: 'rgba(136,150,168,0.1)', text: '#5C6B7A' },
};

export default function PoliciesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { classFilter } = useClassFilter();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [classTab, setClassTab] = useState('all');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyDetail, setPolicyDetail] = useState(null);

  const [summary, setSummary] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    const cls = classFilter && classFilter !== 'all' ? `&insurance_class=${classFilter}` : '';
    const qp = search.trim() ? `&q=${encodeURIComponent(search.trim())}` : '';
    const t = setTimeout(() => {
      Promise.all([
        api.get(`/policies?limit=200${cls}${qp}`),
        api.get(`/policies/summary?x=1${cls}`).catch(() => ({ data: null })),
      ]).then(([r, s]) => { setPolicies(r.data); setSummary(s.data); })
        .catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
    }, search.trim() ? 350 : 0);
    return () => clearTimeout(t);
  }, [api, classFilter, search]);

  // Derive class tabs from data
  const classCounts = useMemo(() => {
    const counts = {};
    policies.forEach(p => {
      const cls = p.class || 'Unknown';
      counts[cls] = (counts[cls] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [policies]);

  // Filter policies
  const filtered = useMemo(() => {
    let result = policies;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p => p.policy_number?.toLowerCase().includes(q) || p.business_name?.toLowerCase().includes(q) || p.abn?.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter);
    }
    if (classTab !== 'all') {
      result = result.filter(p => p.class === classTab);
    }
    return result;
  }, [policies, search, statusFilter, classTab]);

  // Summary KPIs
  const totalGWP = useMemo(() => filtered.reduce((s, p) => s + (p.annual_premium || 0), 0), [filtered]);
  const activeCount = useMemo(() => filtered.filter(p => p.status === 'active').length, [filtered]);
  const sort = useSort(filtered, 'inception_date');

  const selectPolicy = async (policy) => {
    setSelectedPolicy(policy);
    try {
      const { data } = await api.get(`/drilldown/policy/${policy.policy_id}/detail`);
      setPolicyDetail(data);
    } catch (err) { console.error('API error:', err?.message); }
  };

  if (loading) return <div className="space-y-3" data-testid="policies-loading">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-md" />)}</div>;

  // ─── Policy Detail View ───
  if (selectedPolicy && policyDetail) {
    const p = policyDetail.policy;
    const sc = STATUS_COLORS[p.status] || STATUS_COLORS.active;
    const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
    return (
      <div className="space-y-4" data-testid="policy-detail">
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelectedPolicy(null); setPolicyDetail(null); }} className="p-2 rounded-md hover:bg-gray-100"><ArrowLeft className="w-4 h-4" style={{ color: '#1A4C7C' }} /></button>
          <div>
            <div className="flex items-center gap-2"><h2 className="text-lg font-semibold font-mono" style={{ color: '#0D1B2A' }}>{p.policy_number}</h2><span className="px-2 py-0.5 rounded text-[10px] font-semibold capitalize" style={{ background: sc.bg, color: sc.text }}>{p.status}</span>
              <PolicyStatusControl api={api} policy={p} onChanged={(updated) => {
                setPolicyDetail(prev => ({ ...prev, policy: updated }));
                setPolicies(prev => prev.map(x => x.policy_id === updated.policy_id ? { ...x, status: updated.status } : x));
              }} />
            </div>
            <p className="text-sm" style={{ color: '#5C6B7A' }}>{p.business_name} — {p.class}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Premium" value={`$${(p.annual_premium || 0).toLocaleString()}`} color="#0D1B2A" />
          <KPI label="Sum Insured" value={`$${((p.sum_insured || 0) / 1000).toFixed(0)}K`} color="#1A4C7C" />
          <KPI label="Score" value={`${p.composite_score || 0}/10K`} color="#00B4D8" />
          <KPI label="Claims" value={policyDetail.summary?.claims_count || 0} color={policyDetail.summary?.claims_count > 0 ? '#E24B4A' : '#1D9E75'} />
          <KPI label="Endorsements" value={policyDetail.summary?.endorsements_count || 0} color="#5C6B7A" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Policy Details</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Det label="Policy Number" value={p.policy_number} /><Det label="Class" value={p.class} />
                <Det label="Inception" value={new Date(p.inception_date).toLocaleDateString('en-AU')} /><Det label="Expiry" value={new Date(p.expiry_date).toLocaleDateString('en-AU')} />
                <Det label="Business" value={p.entity_id
                  ? <button onClick={() => navigate(`/client/${p.entity_id}`)} className="hover:underline text-left" style={{ color: '#1A4C7C' }} data-testid="view-client-link">{p.business_name}</button>
                  : p.business_name} /><Det label="ABN" value={<RegistryLink abn={p.abn} />} />
                <Det label="Broker" value={p.broker_name || '—'} /><Det label="Decision" value={p.decision} />
                <Det label="Channel" value={p.channel === 'affinity' ? `Affinity — ${p.affinity_partner?.name || ''}` : 'Broker'} />
                <Det label="Partner commission" value={p.channel === 'affinity' ? `${p.affinity_commission_pct ?? 0}% = $${(p.affinity_commission_amount || 0).toLocaleString()}` : '—'} />
                <Det label="Industry" value={p.industry || '—'} /><Det label="Created" value={new Date(p.created_at).toLocaleString()} />
              </div>
            </Card>
            {/* Documents */}
            <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Documents</h3>
              <div className="flex flex-wrap gap-2">
                <a href={`${API_BASE}/documents/policy-schedule/${p.policy_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }}><Download className="w-3.5 h-3.5" />Policy Schedule</a>
                <a href={`${API_BASE}/documents/certificate/${p.policy_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(29,158,117,0.08)', color: '#1D9E75' }}><Download className="w-3.5 h-3.5" />Certificate of Currency</a>
                <a href={`${API_BASE}/documents/closing-pack/${p.policy_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(0,180,216,0.08)', color: '#00B4D8' }}><Download className="w-3.5 h-3.5" />Broker Closing Pack</a>
                <a href={`${API_BASE}/documents/debit-note/${p.policy_id}`} target="_blank" rel="noreferrer" data-testid="policy-debit-note-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(232,160,32,0.08)', color: '#A8720E' }}><Download className="w-3.5 h-3.5" />Closing / Debit Note</a>
                {p.status === 'cancelled' && (
                  <>
                    <a href={`${API_BASE}/documents/credit-note/${p.policy_id}`} target="_blank" rel="noreferrer" data-testid="policy-credit-note-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(226,75,74,0.08)', color: '#B03230' }}><Download className="w-3.5 h-3.5" />Credit Note</a>
                    <a href={`${API_BASE}/documents/cancellation-notice/${p.policy_id}`} target="_blank" rel="noreferrer" data-testid="policy-cancellation-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(226,75,74,0.08)', color: '#B03230' }}><Download className="w-3.5 h-3.5" />Cancellation Notice</a>
                  </>
                )}
                {p.status === 'active' && (
                  <>
                    <a href={`${API_BASE}/documents/s59-notice/${p.policy_id}`} target="_blank" rel="noreferrer" data-testid="policy-s59-notice-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(226,75,74,0.08)', color: '#B03230' }} title="s59 Notice of Intention to Cancel — non-payment"><Download className="w-3.5 h-3.5" />s59 Notice</a>
                    <a href={`${API_BASE}/documents/renewal-declaration/${p.policy_id}`} target="_blank" rel="noreferrer" data-testid="policy-renewal-dec-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }} title="Renewal Declaration — confirm-or-correct incl. duty split"><Download className="w-3.5 h-3.5" />Renewal Declaration</a>
                    <button onClick={async () => {
                      const reason = window.prompt('Reason for non-renewal (printed in general terms on the s58 notice):', 'The risk falls outside our current underwriting appetite for this class.');
                      if (!reason) return;
                      try {
                        const resp = await api.post(`/documents/non-renewal/${p.policy_id}`, { reason }, { responseType: 'blob' });
                        const url = URL.createObjectURL(resp.data); const a = document.createElement('a');
                        a.href = url; a.download = `NonRenewal_${p.policy_number}.pdf`; a.click(); URL.revokeObjectURL(url);
                      } catch { toast.error('Could not generate the non-renewal notice'); }
                    }} data-testid="policy-nonrenewal-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium" style={{ background: 'rgba(226,75,74,0.08)', color: '#B03230' }} title="s58 notice that renewal will not be offered"><Download className="w-3.5 h-3.5" />Non-Renewal Notice</button>
                  </>
                )}
                <EmailDocButton docType="closing_pack" refId={p.policy_id} docLabel="Broker Closing Pack" />
                <PortalLinkButton policyId={p.policy_id} />
              </div>
              <h4 className="text-[10px] font-semibold uppercase tracking-wider mt-4 mb-1" style={{ color: '#5C6B7A' }}>Uploaded Documents</h4>
              <RecordDocuments recordType="policy" recordId={p.policy_id} />
            </Card>
            <InterestedPartiesCard api={api} policyId={p.policy_id} />
            {(p.class || '').toLowerCase().includes('marine') && <OpenCoverPanel api={api} policy={p} />}
            {(p.class || '').toLowerCase().includes('crop') && <CropSeasonPanel api={api} policy={p} />}
            {(p.class || '').toLowerCase().includes('trade') && (p.class || '').toLowerCase().includes('credit') && <BuyerLedgerPanel api={api} policy={p} />}
            <EndorsementsCard policy={p} onIssued={() => selectPolicy(p)} />
            {/* Claims */}
            {policyDetail.claims?.length > 0 && (
              <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Claims ({policyDetail.claims.length})</h3>
                {policyDetail.claims.map(cl => (
                  <div key={cl.claim_id} className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                    <div><span className="font-mono text-xs" style={{ color: '#1A4C7C' }}>{cl.claim_reference}</span><span className="text-xs ml-2 capitalize" style={{ color: '#5C6B7A' }}>{cl.claim_type}</span></div>
                    <div className="flex items-center gap-3"><span className="text-xs font-mono" style={{ color: '#0D1B2A' }}>${(cl.reserve_amount || 0).toLocaleString()}</span><span className="px-1.5 py-0.5 rounded text-[10px] font-medium capitalize" style={{ background: (cl.status === 'settled' ? '#1D9E75' : '#E8A020') + '12', color: cl.status === 'settled' ? '#1D9E75' : '#E8A020' }}>{cl.status?.replace(/_/g, ' ')}</span></div>
                  </div>
                ))}
              </Card>
            )}
          </div>
          {/* Entity info */}
          <div>
            {policyDetail.entity && (
              <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Entity</h3>
                <div className="space-y-2">
                  <Det label="Legal Name" value={policyDetail.entity.legal_name} />
                  <Det label="ABN" value={<RegistryLink abn={policyDetail.entity.abn || policyDetail.entity.abn_nzbn} />} />
                  <Det label="Industry" value={policyDetail.entity.industry_code || '—'} />
                  <Det label="Location" value={`${policyDetail.entity.city || ''}, ${policyDetail.entity.state || ''}`} />
                  <Det label="Employees" value={policyDetail.entity.employee_count || '—'} />
                  <Det label="Hazard Score" value={policyDetail.entity.hazard_score || '—'} />
                </div>
              </Card>
            )}
            <Card className="p-4 rounded-md mt-3" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Financial Summary</h3>
              <Det label="Reserves" value={`$${(policyDetail.summary?.total_reserve || 0).toLocaleString()}`} />
              <Det label="Paid" value={`$${(policyDetail.summary?.total_paid || 0).toLocaleString()}`} />
              <Det label="Premium Adjustments" value={`$${(policyDetail.summary?.premium_adjustments || 0).toLocaleString()}`} />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="policies-page">
      {/* Summary KPIs — whole book, not the visible page */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="px-4 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#5C6B7A' }}>Policies</p>
          <p className="text-lg font-semibold font-mono" style={{ color: '#0D1B2A' }} data-testid="policies-total-count">{(summary?.total_count ?? filtered.length).toLocaleString()}</p>
        </Card>
        <Card className="px-4 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#5C6B7A' }}>GWP</p>
          <p className="text-lg font-semibold font-mono" style={{ color: '#FF9933' }} data-testid="policies-total-gwp">${((summary?.active_gwp ?? totalGWP) / 1e6).toFixed(1)}M</p>
        </Card>
        <Card className="px-4 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#5C6B7A' }}>Active</p>
          <p className="text-lg font-semibold font-mono" style={{ color: '#1D9E75' }} data-testid="policies-active-count">{(summary?.active_count ?? activeCount).toLocaleString()}</p>
        </Card>
      </div>

      <CancellationNoticesCard />

      {/* Search + Status Filter */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5C6B7A' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by policy number, business name, or ABN..."
            className="w-full pl-10 pr-4 py-2 text-sm rounded-md border outline-none focus:border-[#2A6FC4]"
            style={{ color: '#0D1B2A', borderColor: 'rgba(136,150,168,0.2)' }}
            data-testid="policy-search"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-xs rounded-md border" style={{ color: '#0D1B2A', borderColor: 'rgba(136,150,168,0.2)' }}
          data-testid="policy-status-filter">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
        <SavedViews page="policies"
          getConfig={() => ({ search, statusFilter, classTab, sortKey: sort.sortKey, sortDir: sort.sortDir })}
          applyConfig={(c) => {
            setSearch(c.search || '');
            setStatusFilter(c.statusFilter || 'all');
            setClassTab(c.classTab || 'all');
            sort.set(c.sortKey || 'inception_date', c.sortDir || 'desc');
          }} />
      </div>

      {/* Class Tabs */}
      {classCounts.length > 1 && (
        <div className="flex gap-0 overflow-x-auto border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
          <button onClick={() => setClassTab('all')}
            className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${classTab === 'all' ? 'border-[#2A6FC4] text-[#0D1B2A]' : 'border-transparent text-[#5C6B7A] hover:text-[#0D1B2A]'}`}
            data-testid="class-tab-all">
            All <span className="ml-1 text-[10px] opacity-60">{(summary?.total_count ?? policies.length).toLocaleString()}</span>
          </button>
          {classCounts.slice(0, 8).map(([cls, count]) => (
            <button key={cls} onClick={() => setClassTab(cls)}
              className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${classTab === cls ? 'border-[#2A6FC4] text-[#0D1B2A]' : 'border-transparent text-[#5C6B7A] hover:text-[#0D1B2A]'}`}>
              {cls} <span className="ml-1 text-[10px] opacity-60">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="policies-table">
            <thead>
              <tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
                <SortableTh label="Policy #" sortKey="policy_number" sort={sort} />
                <SortableTh label="Business" sortKey="business_name" sort={sort} />
                <SortableTh label="Class" sortKey="class" sort={sort} />
                <SortableTh label="Inception" sortKey="inception_date" sort={sort} />
                <SortableTh label="Expiry" sortKey="expiry_date" sort={sort} />
                <SortableTh label="Premium" sortKey="annual_premium" sort={sort} align="right" />
                <SortableTh label="Status" sortKey="status" sort={sort} />
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-0"><EmptyState type="policies" /></td></tr>
              ) : sort.sorted.map((p) => {
                const sc = STATUS_COLORS[p.status] || STATUS_COLORS.active;
                return (
                  <tr key={p.policy_id} onClick={() => selectPolicy(p)} className="border-b hover:bg-[#FAFBFC] cursor-pointer transition-colors group" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                    <td className="py-2.5 px-4 font-mono text-xs font-medium" style={{ color: '#1A4C7C' }}>{p.policy_number}</td>
                    <td className="py-2.5 px-4 text-xs font-medium" style={{ color: '#0D1B2A' }}>{p.business_name}{p.channel === 'affinity' && <span className="ml-1.5 text-[9px] font-bold uppercase px-1 py-0.5 rounded-sm" style={{ background: 'rgba(108,75,181,0.1)', color: '#6C4BB5' }}>Affinity</span>}</td>
                    <td className="py-2.5 px-4 text-[11px]" style={{ color: '#5C6B7A' }}>{p.class}</td>
                    <td className="py-2.5 px-4 text-[11px]" style={{ color: '#5C6B7A' }}>{new Date(p.inception_date).toLocaleDateString('en-AU')}</td>
                    <td className="py-2.5 px-4 text-[11px]" style={{ color: '#5C6B7A' }}>{new Date(p.expiry_date).toLocaleDateString('en-AU')}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-xs" style={{ color: '#0D1B2A' }}>${p.annual_premium?.toLocaleString()}</td>
                    <td className="py-2.5 px-4">
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium capitalize" style={{ background: sc.bg, color: sc.text }}>{p.status}</span>
                    </td>
                    <td className="py-1 px-2"><ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" style={{ color: '#1A4C7C' }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PolicyStatusControl({ api, policy, onChanged }) {
  const [open, setOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const options = ['active', 'cancelled', 'expired'].filter(s => s !== policy.status);

  const apply = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.put(`/policies/${policy.policy_id}/status`, { status: newStatus, reason });
      setOpen(false); setNewStatus(''); setReason('');
      onChanged(data);
    } catch (err) { setError(err?.response?.data?.detail || 'Failed to update status'); }
    setBusy(false);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="policy-status-change-btn"
        className="px-2 py-0.5 rounded text-[10px] font-medium border transition-colors hover:bg-[#F5F7FA]"
        style={{ color: '#1A4C7C', borderColor: 'rgba(26,76,124,0.3)' }}>
        Change status
      </button>
      {open && (
        <div className="absolute left-0 top-7 z-30 w-64 p-3 rounded-md bg-white shadow-lg space-y-2" style={{ border: '1px solid rgba(136,150,168,0.25)' }} data-testid="policy-status-popover">
          <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#5C6B7A' }}>Set policy status</p>
          <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full px-2 py-1.5 text-xs rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' }} data-testid="policy-status-select">
            <option value="">Select…</option>
            {options.map(s => <option key={s} value={s}>{s === 'active' ? 'Active (reinstate)' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          {newStatus === 'cancelled' && (
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Cancellation reason (required)"
              className="w-full px-2 py-1.5 text-xs rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' }} data-testid="policy-cancel-reason-input" />
          )}
          {error && <p className="text-[10px]" style={{ color: '#E24B4A' }} data-testid="policy-status-error">{error}</p>}
          <div className="flex gap-2">
            <button onClick={apply} disabled={busy || !newStatus || (newStatus === 'cancelled' && !reason.trim())} data-testid="policy-status-apply-btn"
              className="px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white disabled:opacity-40" style={{ background: '#1A4C7C' }}>Apply</button>
            <button onClick={() => { setOpen(false); setError(''); }} className="px-2 py-1.5 rounded-sm text-[10px] font-medium" style={{ color: '#5C6B7A' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (<Card className="px-4 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
    <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#5C6B7A' }}>{label}</p>
    <p className="text-lg font-semibold mt-0.5 font-mono" style={{ color }}>{value}</p>
  </Card>);
}

function Det({ label, value }) {
  return (<div className="flex justify-between py-1 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
    <span className="text-[11px]" style={{ color: '#5C6B7A' }}>{label}</span>
    <span className="text-xs font-medium" style={{ color: '#0D1B2A' }}>{value}</span>
  </div>);
}

const inputCls = "px-2 py-1.5 text-xs rounded-sm border w-full focus:outline-none focus:ring-2 focus:ring-[#1A4C7C]";
const inputStyle = { borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' };

function InterestedPartiesCard({ api, policyId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: '', abn: '', interest_type: 'Mortgagee', noted_date: new Date().toISOString().slice(0, 10) });
  const [pdLimit, setPdLimit] = useState('');
  const [injLimit, setInjLimit] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/policies/${policyId}/interested-parties`);
      setData(d);
      setPdLimit(d.tp_limits?.tp_property_damage_limit ?? '');
      setInjLimit(d.tp_limits?.tp_injury_limit ?? '');
    } catch (err) { console.error('API error:', err?.message); }
  }, [api, policyId]);
  useEffect(() => { load(); }, [load]);

  const addParty = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await api.post(`/policies/${policyId}/interested-parties`, form);
      setForm({ name: '', abn: '', interest_type: 'Mortgagee', noted_date: new Date().toISOString().slice(0, 10) });
      await load();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to add party'); }
    setBusy(false);
  };

  const removeParty = async (partyId) => {
    setBusy(true);
    try { await api.delete(`/policies/${policyId}/interested-parties/${partyId}`); await load(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to remove'); }
    setBusy(false);
  };

  const saveLimits = async () => {
    setError(''); setBusy(true);
    try {
      await api.put(`/policies/${policyId}/tp-limits`, {
        tp_property_damage_limit: pdLimit === '' ? null : Number(pdLimit),
        tp_injury_limit: injLimit === '' ? null : Number(injLimit),
      });
      await load();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save limits'); }
    setBusy(false);
  };

  if (!data) return null;
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="interested-parties-card">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4" style={{ color: '#1A4C7C' }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Interested Parties & TP Limits</h3>
        <span className="text-[10px]" style={{ color: '#5C6B7A' }}>printed on the Certificate of Currency</span>
      </div>

      {data.interested_parties.length === 0 ? (
        <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>No interested parties noted on this policy.</p>
      ) : (
        <div className="mb-3 space-y-1">
          {data.interested_parties.map(ip => (
            <div key={ip.party_id} className="flex items-center justify-between py-1.5 px-2 rounded-sm" style={{ background: '#F5F7FA' }} data-testid={`interested-party-${ip.party_id}`}>
              <div className="text-xs" style={{ color: '#0D1B2A' }}>
                <span className="font-medium">{ip.name}</span>
                <span className="ml-2" style={{ color: '#5C6B7A' }}>{ip.interest_type}{ip.abn ? ` · ABN ${ip.abn}` : ''} · noted {ip.noted_date}</span>
              </div>
              <button onClick={() => removeParty(ip.party_id)} disabled={busy} title="Remove party" data-testid={`remove-party-${ip.party_id}`}
                className="p-1 rounded hover:bg-[rgba(226,75,74,0.1)]"><X className="w-3.5 h-3.5" style={{ color: '#E24B4A' }} /></button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={addParty} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end mb-4">
        <input className={inputCls} style={inputStyle} placeholder="Party name" value={form.name} required
          onChange={e => setForm({ ...form, name: e.target.value })} data-testid="party-name-input" />
        <input className={inputCls} style={inputStyle} placeholder="ABN (optional)" value={form.abn}
          onChange={e => setForm({ ...form, abn: e.target.value })} data-testid="party-abn-input" />
        <select className={inputCls} style={inputStyle} value={form.interest_type}
          onChange={e => setForm({ ...form, interest_type: e.target.value })} data-testid="party-interest-type-select">
          {(data.interest_types || []).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="date" className={inputCls} style={inputStyle} value={form.noted_date}
          onChange={e => setForm({ ...form, noted_date: e.target.value })} data-testid="party-noted-date-input" />
        <button type="submit" disabled={busy} data-testid="add-party-button"
          className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-sm text-xs font-semibold text-white" style={{ background: '#1A4C7C' }}>
          <Plus className="w-3.5 h-3.5" />Add Party
        </button>
      </form>

      <div className="pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
        <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#5C6B7A' }}>Third-Party Liability Limits</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 items-end">
          <div>
            <label className="text-[10px]" style={{ color: '#5C6B7A' }}>TP Property Damage ($)</label>
            <input type="number" min="0" className={inputCls} style={inputStyle} value={pdLimit}
              onChange={e => setPdLimit(e.target.value)} placeholder="e.g. 20000000" data-testid="tp-pd-limit-input" />
          </div>
          <div>
            <label className="text-[10px]" style={{ color: '#5C6B7A' }}>TP Injury ($)</label>
            <input type="number" min="0" className={inputCls} style={inputStyle} value={injLimit}
              onChange={e => setInjLimit(e.target.value)} placeholder="e.g. 20000000" data-testid="tp-injury-limit-input" />
          </div>
          <button onClick={saveLimits} disabled={busy} data-testid="save-tp-limits-button"
            className="px-3 py-1.5 rounded-sm text-xs font-semibold text-white" style={{ background: '#1D9E75' }}>
            Save Limits
          </button>
        </div>
      </div>

      {error && <p className="text-xs mt-2" style={{ color: '#E24B4A' }} data-testid="interested-parties-error">{error}</p>}
    </Card>
  );
}
